import { useCallback, useEffect, useRef, useState } from 'react';
import {
  POPUP_STATE_KEY,
  getActiveTab,
  getPopupState,
  setPopupState,
  type IndependentRatingPopupState,
} from './independentRatingStorage';

/**
 * Phase 6.5 — the HOST state machine of the header "Independent Rating" popup.
 *
 * React port of the four `header/view/header.js` members that own the popup's
 * lifecycle (the shared header, so this lights the button on EVERY React page):
 *
 *  - `openComponentSelectorPopup` (:522-532)
 *      $("#sizer_popup #rating_floating-icon").addClass("hide");            // :523
 *      if ($(".component-selector-popup")[0]) {                             // :525
 *        $(".component-selector-popup").addClass("active"); return;         // :526-527  ← EARLY RETURN
 *      }
 *      var v = new ViewComponentSelectorPopup();                            // :529
 *      $("body").append(v.$el); v.show();                                   // :530-531
 *    Bound to BOTH `click #component_selector_button` and
 *    `click #sizer_popup #rating_floating-icon` (:56-58) — the floating icon is
 *    NOT a second surface, it is the only way back from `minimize`.
 *
 *  - `renderFloatingIcon` (:560-570) — append the icon once, then show it iff
 *    the stored state is `'minimize'`.
 *
 *  - `changeState` (:572-575) — `'open'` → open the popup; anything else →
 *    `removeClass('active')`.
 *
 *  - the page-load restore + cross-tab listener (:232-246):
 *      if (get('independent_rating_popup_state')) changeState(get(...));
 *      self.renderFloatingIcon();
 *      fn.local_storage.add_listener('independent_rating_popup_state', …)
 *    `add_listener` is a `window` `storage` listener firing on `e.key === key`
 *    OR `!e.key` (a whole-storage clear) — js_scripts/local_storage.js:33-38 —
 *    so it only ever fires in OTHER tabs.
 *
 * ★ AUTO-REOPEN IS REAL AND OBSERVED: a page load with the stored state `'open'`
 * re-creates the popup ALREADY `.active`, on the tab named by
 * `active_independent_rating` (discovery §4.1).
 *
 * ★ MIRRORED LEGACY DESYNC (orchestrator decision 2). Re-opening via the button
 * or the floating icon takes the `:525-528` early return, which NEVER re-writes
 * `'open'`. So: minimize → icon-click leaves the state at `'minimize'` while the
 * popup is visible; CLOSE → button-click leaves it at `'close'`, meaning that
 * session will NOT auto-reopen on the next reload. Reproduced EXACTLY — the
 * `'open'` write lives only in the popup's own construction effect
 * (component_selector.js:111), which runs once per page load. Do NOT "fix" this
 * without a product call.
 *
 * ★ The floating icon's visibility is NOT purely derived from the stored state:
 * `openComponentSelectorPopup` hides it imperatively (:523) on a path that does
 * not write the state, so it is tracked as its own piece of host state — which
 * is exactly how the desync above becomes observable.
 */
export interface IndependentRatingHost {
  /** true once the popup has been constructed. NEVER returns to false —
   * create-once/never-destroy (header.js:525-528). */
  mounted: boolean;
  /** the `.active` class (opacity/z-index state machine in common.css). */
  active: boolean;
  /** `#rating_floating-icon` visible (i.e. NOT `.hide`). */
  iconVisible: boolean;
  /** `localStorage.active_independent_rating || 'fan'`, snapshotted at the
   * moment of construction (component_selector.popup.js:42 reads it ONCE). */
  initialTab: string;
  /** header.js:522 — the button AND the floating icon. */
  open: () => void;
  /** component_selector.popup.js:52-58 — the CLOSE button. */
  close: () => void;
  /** component_selector.js:284-289 — the minimize icon. */
  minimize: () => void;
}

export function useIndependentRating(): IndependentRatingHost {
  const [mounted, setMounted] = useState(false);
  const [active, setActive] = useState(false);
  const [iconVisible, setIconVisible] = useState(false);
  const [initialTab, setInitialTab] = useState('fan');
  /** synchronous mirror of `mounted` — `open()` must see it inside the same
   * tick (the legacy check is a live DOM query). */
  const mountedRef = useRef(false);

  const open = useCallback(() => {
    // :523 — hide the floating icon FIRST, unconditionally and imperatively.
    setIconVisible(false);
    if (mountedRef.current) {
      // :525-528 — already in the DOM: just re-add `.active` and RETURN.
      // ★ No state write here. See the desync note above.
      setActive(true);
      return;
    }
    // popup.js:42 — the remembered tab, read ONCE at construction.
    setInitialTab(getActiveTab());
    mountedRef.current = true;
    setMounted(true);
    // popup.js:46-50 show() → addClass('active'). The `'open'` state write is
    // the popup's own (renderPopupContent, component_selector.js:111).
    setActive(true);
  }, []);

  const close = useCallback(() => {
    // popup.js:52-58 hide(): drop `.active`, hide the floating icon, state
    // `'close'`. The popup itself is NOT removed from the DOM.
    setActive(false);
    setIconVisible(false);
    setPopupState('close');
  }, []);

  const minimize = useCallback(() => {
    // component_selector.js:284-289: drop `.active`, SHOW the floating icon,
    // state `'minimize'`.
    setActive(false);
    setIconVisible(true);
    setPopupState('minimize');
  }, []);

  // ---- header.js:232-246 — page-load restore + the cross-tab storage listener.
  useEffect(() => {
    /** header.js:572-575 changeState. */
    const changeState = (state: IndependentRatingPopupState | null) => {
      if (state === 'open') open();
      else setActive(false);
    };
    /** header.js:566-569 — the icon shows iff the stored state is 'minimize'. */
    const renderFloatingIcon = () => setIconVisible(getPopupState() === 'minimize');

    const stored = getPopupState();
    if (stored) changeState(stored);
    renderFloatingIcon();

    const onStorage = (e: StorageEvent) => {
      // local_storage.js:33-38 — `e.key == key || !e.key` (null on clear()).
      if (e.key !== POPUP_STATE_KEY && e.key != null) return;
      changeState(getPopupState());
      renderFloatingIcon();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [open]);

  return { mounted, active, iconVisible, initialTab, open, close, minimize };
}
